import {
  MutableRefObject,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import axios, {CancelTokenSource} from 'axios';
import {LoadingButton} from '@mui/lab';
import {Alert, Box, Button, CircularProgress, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {AuthContext} from '../../../../../contexts/auth/context';
import {RoleOnRelationship} from '../../../../../backend/enums/role-on-relationship';
import {Application} from '../../../../../backend/interfaces/application';
import {ApplicationCategory} from '../../../../../backend/interfaces/application-category';
import {CategorizedApplication} from '../../../../../backend/responses/categorized-application';
import {ApplicationsRepository} from '../../../../../repositories/applications';
import {ApplicationCategoriesRepository} from '../../../../../repositories/application-categories';
import {ContentPage} from '../../../../../components/content-page/content-page';
import GenericHeader from '../../../../../components/generic-header/generic-header';
import {LoadingContent} from '../../../../../components/loading-content/loading-content';
import SearchInput from '../../../../../components/search-input/search-input';
import FlatSelectInput from '../../../../../components/flat-select-input/flat-select-input';
import {ApplicationCatalogTable} from './application-catalog-table';
import '../../../../../components/user-applications/user-applications.scss';
import {ApplicationsStateModal} from '../../../../../components/user-applications/modals/applications-state-modal';
import {translations} from '../../../../../i18n/translations';
import {ScreenPaths} from '../../../../../enums/screen-path';

const copy = translations.family_applications;
const PAGE_SIZE = 20;

// A keyed subtree owns each context. The live identity also invalidates reads/writes
// immediately during render, before effect cleanup when the selected senior changes.
export default function ApplicationsInformalCaregiverScreen() {
  const {t, i18n} = useTranslation();
  const {state} = useContext(AuthContext);
  const {seniorId, applicationId} = useParams<{seniorId: string; applicationId?: string}>();

  const relationship = state.session?.selectedRelationship;
  const selectedId = relationship?.withUser.id;
  const affiliations = relationship?.withUser.affiliations;
  const entities = affiliations
    ?.map((affiliation) => affiliation.entity)
    .filter(
      (entity, index, all) => entity.id && all.findIndex((item) => item.id === entity.id) === index
    );

  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const requestedEntity = query.get('entityId');
  const validEntity = entities?.find((entity) => entity.id === requestedEntity)?.id;
  const entityId = validEntity || (entities?.length === 1 ? entities[0].id : undefined);
  const canManage = relationship?.role === RoleOnRelationship.Administrator;
  const basePath = `${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/${seniorId}${ScreenPaths.InformalCaregiver_Applications}`;

  const identity = JSON.stringify([
    seniorId,
    selectedId,
    entityId,
    applicationId,
    canManage,
    i18n.language,
  ]);
  const liveIdentity = useRef(identity);
  liveIdentity.current = identity;

  // Normalize invalid query IDs; never use the caregiver's selected affiliation.
  useEffect(() => {
    if (seniorId !== selectedId) return;
    if ((requestedEntity || undefined) === entityId) return;
    const next = new URLSearchParams(location.search);
    if (entityId) next.set('entityId', entityId);
    else next.delete('entityId');
    history.replace({pathname: location.pathname, search: next.toString()});
  }, [
    seniorId,
    selectedId,
    requestedEntity,
    entityId,
    location.pathname,
    location.search,
    history,
  ]);

  if (!selectedId || seniorId !== selectedId) return null;

  const entityQuery = entityId ? `?entityId=${encodeURIComponent(entityId)}` : '';
  return (
    <ApplicationPage
      key={identity}
      available={!!affiliations && !!entities?.length && !!entityId}
      {...{
        seniorId,
        entityId: entityId || '',
        applicationId,
        canManage,
        basePath,
        entityQuery,
        identity,
        liveIdentity,
      }}
    >
      {entities && entities.length > 1 && (
        <FlatSelectInput
          label={t(copy.entity)}
          value={entityId || ''}
          displayEmpty
          placeholder={t(copy.choose_entity)}
          options={entities.map((entity) => ({value: entity.id, label: entity.name}))}
          onChange={(event) => {
            const next = new URLSearchParams(location.search);
            if (event.target.value) next.set('entityId', String(event.target.value));
            else next.delete('entityId');
            history.push({pathname: location.pathname, search: next.toString()});
          }}
        />
      )}
      {!affiliations ? (
        <Alert severity="info">{t(copy.missing_affiliations)}</Alert>
      ) : !entities?.length ? (
        <Alert severity="info">{t(copy.no_affiliations)}</Alert>
      ) : !entityId ? (
        <Alert severity="info">{t(copy.choose_entity)}</Alert>
      ) : null}
    </ApplicationPage>
  );
}

type ContextProps = {
  seniorId: string;
  entityId: string;
  applicationId?: string;
  canManage: boolean;
  basePath: string;
  entityQuery: string;
  identity: string;
  liveIdentity: MutableRefObject<string>;
};

function ApplicationPage(props: ContextProps & {children: ReactNode; available: boolean}) {
  const {t} = useTranslation();
  const [search, setSearch] = useState('');
  const [headerLoading, setHeaderLoading] = useState(props.available);

  const isDetailPage = !!props.applicationId;
  const listPath = `${props.basePath}${props.entityQuery}`;
  const titleCrumbs = [
    {label: t(copy.title), link: listPath},
    ...(props.applicationId
      ? [
          {
            label: t(translations.application_tabs.tabs.details),
            link: `${props.basePath}/${props.applicationId}${props.entityQuery}`,
          },
        ]
      : []),
  ];

  return (
    <ContentPage fullWidth>
      <GenericHeader
        isLoading={headerLoading}
        showBackButton={isDetailPage}
        titleCrumbs={titleCrumbs}
        leftColumn={
          !isDetailPage && props.available ? (
            <Stack direction="row" gap={2} sx={{width: '100%', pt: '10px'}}>
              <SearchInput
                placeholder={t(translations.user_applications.search_input_placeholder)}
                inputProps={{
                  'aria-label': t(translations.user_applications.search_input_placeholder),
                }}
                onSearchAction={(value) => setSearch(value.trim())}
              />
            </Stack>
          ) : null
        }
        rightColumn={null}
      />
      <Box className="listing-container">
        <Stack className="listing-container-centered" spacing={3}>
          {props.children}
          {props.available &&
            (props.applicationId ? (
              <ApplicationDetail {...props} onInitialLoadingChange={setHeaderLoading} />
            ) : (
              <ApplicationCatalog
                key={search}
                {...props}
                search={search}
                onInitialLoadingChange={setHeaderLoading}
              />
            ))}
        </Stack>
      </Box>
    </ContentPage>
  );
}

function useCurrentContext({identity, liveIdentity}: ContextProps) {
  const mounted = useRef(false);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return () => mounted.current && liveIdentity.current === identity;
}

function ApplicationCatalog(
  props: ContextProps & {search: string; onInitialLoadingChange: (loading: boolean) => void}
) {
  const {t} = useTranslation();
  const {onInitialLoadingChange} = props;
  const isCurrent = useCurrentContext(props);
  const [rows, setRows] = useState<CategorizedApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  const [confirmation, setConfirmation] = useState<Application>();
  const [pending, setPending] = useState(false);
  const [writeResult, setWriteResult] = useState<'success' | 'error'>();

  useEffect(() => {
    onInitialLoadingChange(loading && rows.length === 0);
  }, [loading, onInitialLoadingChange, rows.length]);

  const writing = useRef(false);
  const inFlight = useRef(false);
  const readSource = useRef<CancelTokenSource>();

  const load = async () => {
    if (inFlight.current || writing.current || confirmation || !isCurrent()) return;
    inFlight.current = true;
    const source = axios.CancelToken.source();
    readSource.current = source;
    setLoading(true);
    setError(false);

    try {
      const result = await ApplicationsRepository.instance.getForSeniorInEntity(
        props.seniorId,
        props.entityId,
        offset,
        PAGE_SIZE,
        props.search,
        source.token
      );

      if (!isCurrent() || source.token.reason) return;

      setRows((previous) => {
        const merged = new Map(previous.map((row) => [row.application.id, row]));
        result.data.forEach((row) => {
          if (row.application?.id) merged.set(row.application.id, row);
        });
        return Array.from(merged.values());
      });
      setTotal(result.totalCount);
      // Advance by the actual server records, not the deduplicated display count.
      setOffset(offset + result.data.length);
      setExhausted(result.data.length === 0);
      setLoaded(true);
    } catch {
      if (isCurrent() && !source.token.reason) setError(true);
    } finally {
      if (isCurrent() && !source.token.reason) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    load();
    return () => {
      readSource.current?.cancel();
      inFlight.current = false;
    };
    // The keyed catalog remounts for search and every senior/entity/language change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askToggle = (id: string) => {
    if (!isCurrent() || !props.canManage || writing.current || inFlight.current) return;
    const application = rows.find((row) => row.application.id === id)?.application;
    if (application && typeof application.isActive === 'boolean') setConfirmation(application);
  };

  const toggle = async () => {
    if (
      !isCurrent() ||
      !props.canManage ||
      !confirmation?.id ||
      typeof confirmation.isActive !== 'boolean' ||
      writing.current
    )
      return;
    const {id, isActive} = confirmation;
    const next = !isActive;
    writing.current = true;
    setConfirmation(undefined);
    setPending(true);
    setWriteResult(undefined);
    try {
      await ApplicationsRepository.instance.updateActiveStateForUser(props.seniorId, id, next);
      if (!isCurrent()) return;
      // Only a successful PATCH changes local state; failures retain the server's last value.
      setRows((previous) =>
        previous.map((row) =>
          row.application.id === id
            ? {...row, application: {...row.application, isActive: next}}
            : row
        )
      );
      setWriteResult('success');
    } catch {
      if (isCurrent()) setWriteResult('error');
    } finally {
      if (isCurrent()) {
        writing.current = false;
        setPending(false);
      }
    }
  };

  return (
    <Stack spacing={2}>
      {writeResult && (
        <Alert severity={writeResult}>
          {t(writeResult === 'success' ? copy.write_success : copy.write_error)}
        </Alert>
      )}
      {error && (
        <Alert severity="error" action={<Button onClick={load}>{t(copy.retry)}</Button>}>
          {t(copy.load_error)}
        </Alert>
      )}
      {loading && rows.length === 0 ? (
        <LoadingContent />
      ) : (
        (rows.length > 0 || (loaded && !error)) && (
          <ApplicationCatalogTable
            rows={rows}
            canManage={props.canManage}
            disabled={pending || loading}
            basePath={props.basePath}
            entityQuery={props.entityQuery}
            onToggle={askToggle}
          />
        )
      )}
      <ApplicationsStateModal
        open={!!confirmation && !pending}
        state={confirmation?.isActive ? 'disable' : 'enable'}
        applicationName={confirmation?.name || ''}
        description={t(copy.confirm)}
        onClose={() => setConfirmation(undefined)}
        onConfirm={toggle}
      />
      {pending && <CircularProgress aria-label={t(copy.access)} />}
      {!error && loaded && !exhausted && offset < total && (
        <Box sx={{display: 'flex', justifyContent: 'center'}}>
          <LoadingButton
            variant="outlined"
            loading={loading && rows.length > 0}
            onClick={load}
            disabled={pending || loading}
          >
            {t(copy.more)}
          </LoadingButton>
        </Box>
      )}
    </Stack>
  );
}

function ApplicationDetail(
  props: ContextProps & {onInitialLoadingChange: (loading: boolean) => void}
) {
  const {t} = useTranslation();
  const {onInitialLoadingChange} = props;
  const isCurrent = useCurrentContext(props);
  const [application, setApplication] = useState<Application>();
  const [category, setCategory] = useState<ApplicationCategory>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [pending, setPending] = useState(false);
  const [writeResult, setWriteResult] = useState<'success' | 'error'>();
  const writing = useRef(false);

  useEffect(() => {
    onInitialLoadingChange(loading && !application);
  }, [application, loading, onInitialLoadingChange]);

  useEffect(() => {
    let cancelled = false;
    const source: CancelTokenSource = axios.CancelToken.source();

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const result = await ApplicationsRepository.instance.getByIdForSeniorInEntity(
          props.seniorId,
          props.entityId,
          props.applicationId!,
          source.token
        );
        if (cancelled || !isCurrent()) return;
        const categoryResult = result.categoryId
          ? await ApplicationCategoriesRepository.instance.getById(
              result.categoryId,
              true,
              source.token
            )
          : undefined;
        if (cancelled || !isCurrent()) return;
        setApplication(result);
        setCategory(categoryResult);
      } catch {
        if (!cancelled && isCurrent()) setError(true);
      } finally {
        if (!cancelled && isCurrent()) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      source.cancel();
    };
    // The detail is keyed by the full context; reload only retries failed reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const toggle = async () => {
    if (
      !isCurrent() ||
      !props.canManage ||
      !application ||
      typeof application.isActive !== 'boolean' ||
      writing.current
    )
      return;
    writing.current = true;
    const next = !application.isActive;
    setConfirm(false);
    setPending(true);
    setWriteResult(undefined);
    try {
      await ApplicationsRepository.instance.updateActiveStateForUser(
        props.seniorId,
        props.applicationId!,
        next
      );
      if (!isCurrent()) return;
      // The PATCH is 204, not an application record. No refresh failure can undo it.
      setApplication({...application, isActive: next});
      setWriteResult('success');
    } catch {
      if (isCurrent()) setWriteResult('error');
    } finally {
      if (isCurrent()) {
        writing.current = false;
        setPending(false);
      }
    }
  };

  if (loading) return <LoadingContent />;

  if (error || !application)
    return (
      <Alert
        severity="error"
        action={<Button onClick={() => setReload((value) => value + 1)}>{t(copy.retry)}</Button>}
      >
        {t(copy.load_error)}
      </Alert>
    );

  const image = application.picture || application.iconUrl;
  const iconBackgroundColor = !application.picture
    ? category?.systemButton?.enabledStateColor?.trim() || undefined
    : undefined;

  return (
    <Stack spacing={2}>
      {image && (
        <Box
          component="img"
          src={image}
          alt={application.name || ''}
          sx={{
            maxWidth: 320,
            maxHeight: 240,
            objectFit: 'contain',
            alignSelf: 'flex-start',
            backgroundColor: iconBackgroundColor,
          }}
        />
      )}
      <Typography variant="h5">{application.name || '—'}</Typography>
      <Typography>{category?.name || '—'}</Typography>
      <Typography sx={{whiteSpace: 'pre-wrap'}}>{application.description || '—'}</Typography>
      {props.canManage && (
        <>
          <Typography>
            {t(copy.access)}:{' '}
            {application.isActive === true
              ? t(copy.enabled)
              : application.isActive === false
                ? t(copy.disabled)
                : '—'}
          </Typography>
          <Button
            variant="contained"
            disabled={pending || typeof application.isActive !== 'boolean'}
            onClick={() => setConfirm(true)}
            sx={{alignSelf: 'flex-start'}}
          >
            {pending ? (
              <CircularProgress size={20} />
            ) : (
              t(application.isActive ? copy.disable : copy.enable)
            )}
          </Button>
          {writeResult && (
            <Alert severity={writeResult}>
              {t(writeResult === 'success' ? copy.write_success : copy.write_error)}
            </Alert>
          )}
          <ApplicationsStateModal
            open={confirm && !pending}
            state={application.isActive ? 'disable' : 'enable'}
            applicationName={application.name || ''}
            description={t(copy.confirm)}
            onClose={() => setConfirm(false)}
            onConfirm={toggle}
          />
        </>
      )}
    </Stack>
  );
}
